import * as vd from "virtual-dom";
import { combineLatest as observableCombineLatest, Subject } from "rxjs";
import { filter, map, scan, shareReplay } from "rxjs/operators";
import { SubscriptionHolder } from "../utils/SubscriptionHolder";
export class DOMRenderer {
    constructor(element, renderService, currentFrame$) {
        this._adaptiveOperation$ = new Subject();
        this._render$ = new Subject();
        this._renderAdaptive$ = new Subject();
        this._subscriptions = new SubscriptionHolder();
        this._renderService = renderService;
        this._currentFrame$ = currentFrame$;
        const subs = this._subscriptions;
        const rootNode = vd.create(vd.h("div.mapillary-dom-renderer", []));
        element.appendChild(rootNode);
        this._offset$ = this._adaptiveOperation$.pipe(scan((adaptive, operation) => {
            return operation(adaptive);
        }, {
            elementHeight: element.offsetHeight,
            elementWidth: element.offsetWidth,
            imageAspect: 0,
        }), filter((adaptive) => {
            return adaptive.imageAspect > 0 && adaptive.elementWidth > 0 && adaptive.elementHeight > 0;
        }), map((adaptive) => {
            const elementAspect = adaptive.elementWidth / adaptive.elementHeight;
            const ratio = adaptive.imageAspect / elementAspect;
            let verticalOffset = 0;
            let horizontalOffset = 0;
            if (adaptive.imageAspect > elementAspect) {
                horizontalOffset = (-adaptive.elementWidth * (ratio - 1)) / 2;
            }
            else {
                verticalOffset = (-adaptive.elementHeight * (1 / ratio - 1)) / 2;
            }
            return {
                bottom: verticalOffset,
                left: horizontalOffset,
                right: horizontalOffset,
                top: verticalOffset,
            };
        }));
        const renderAdaptiveSubscription = observableCombineLatest(this._renderAdaptive$.pipe(scan((vNodeHashes, vNodeHash) => {
            if (vNodeHash.vnode == null) {
                delete vNodeHashes[vNodeHash.name];
            }
            else {
                vNodeHashes[vNodeHash.name] = vNodeHash.vnode;
            }
            return vNodeHashes;
        }, {})), this._offset$)
            .pipe(map((vo) => {
            const vNodes = [];
            const hashes = vo[0];
            for (const name in hashes) {
                if (!Object.prototype.hasOwnProperty.call(hashes, name)) {
                    continue;
                }
                vNodes.push(hashes[name]);
            }
            const offset = vo[1];
            const properties = {
                style: {
                    bottom: offset.bottom + "px",
                    left: offset.left + "px",
                    "pointer-events": "none",
                    position: "absolute",
                    right: offset.right + "px",
                    top: offset.top + "px",
                },
            };
            return {
                name: "mapillary-dom-adaptive-renderer",
                vnode: vd.h("div.mapillary-dom-adaptive-renderer", properties, vNodes),
            };
        }))
            .subscribe(this._render$);
        this._vNode$ = this._render$.pipe(scan((vNodeHashes, vNodeHash) => {
            if (vNodeHash.vnode == null) {
                delete vNodeHashes[vNodeHash.name];
            }
            else {
                vNodeHashes[vNodeHash.name] = vNodeHash.vnode;
            }
            return vNodeHashes;
        }, {}), map((hashes) => {
            const vNodes = [];
            for (const name in hashes) {
                if (!Object.prototype.hasOwnProperty.call(hashes, name)) {
                    continue;
                }
                vNodes.push(hashes[name]);
            }
            return vd.h("div.mapillary-dom-renderer", vNodes);
        }));
        this._vPatch$ = this._vNode$.pipe(scan((nodePatch, vNode) => {
            nodePatch.vpatch = vd.diff(nodePatch.vnode, vNode);
            nodePatch.vnode = vNode;
            return nodePatch;
        }, { vnode: vd.h("div.mapillary-dom-renderer", []), vpatch: [] }), map((nodePatch) => nodePatch.vpatch));
        this._element$ = this._vPatch$.pipe(scan((oldElement, vPatch) => {
            return vd.patch(oldElement, vPatch);
        }, rootNode), shareReplay({ bufferSize: 1, refCount: true }));
        subs.push(renderAdaptiveSubscription);
        subs.push(this._element$.subscribe(() => {
            /*noop*/
        }));
        subs.push(this._renderService.size$
            .pipe(map((size) => {
            return (adaptive) => {
                adaptive.elementWidth = size.width;
                adaptive.elementHeight = size.height;
                return adaptive;
            };
        }))
            .subscribe(this._adaptiveOperation$));
    }
    get element$() {
        return this._element$;
    }
    get render$() {
        return this._render$;
    }
    get renderAdaptive$() {
        return this._renderAdaptive$;
    }
    clear(name) {
        this._renderAdaptive$.next({ name: name, vnode: null });
        this._render$.next({ name: name, vnode: null });
    }
    remove() {
        this._subscriptions.unsubscribe();
    }
}
//# sourceMappingURL=DOMRenderer.js.map